import {apiClient} from "../../shared/api/client";
import {requireActorCountryScope} from "../../shared/finance/actorCountryScope";
import {
    normalizePostedRefundPage,
    normalizeRefundPolicy,
    normalizeRefundRequest,
    normalizeRefundRequestList,
} from "./contracts";

const REFUND_REQUESTS_PATH = "/api/v2/admin/refund-requests";
const REFUND_POLICY_PATH = "/api/v2/admin/refund-policy";
const POSTED_REFUNDS_PATH = "/api/v2/admin/refunds";

const scopeKey = (scope) => String(scope);

export const refundKeys = {
    all: (scope) => ["refunds", scopeKey(scope)],
    requestLists: (scope) => ["refunds", scopeKey(scope), "requests", "list"],
    requestList: (scope, filters) => [
        "refunds",
        scopeKey(scope),
        "requests",
        "list",
        filters,
    ],
    requestDetail: (scope, id) => [
        "refunds",
        scopeKey(scope),
        "requests",
        "detail",
        String(id),
    ],
    policy: (scope) => ["refunds", scopeKey(scope), "policy"],
    historyRoot: (scope) => ["refunds", scopeKey(scope), "history"],
    postedEvidence: (scope, paymentBillId, postedRefundId) => [
        "refunds",
        scopeKey(scope),
        "history",
        "evidence",
        String(paymentBillId),
        String(postedRefundId),
    ],
    history: (scope, filters, page) => [
        "refunds",
        scopeKey(scope),
        "history",
        filters,
        page,
    ],
};

const cleanParams = (values) =>
    Object.fromEntries(
        Object.entries(values || {}).filter(
            ([, value]) => value !== "" && value !== null && value !== undefined,
        ),
    );

const tenantScope = (countryScope, actorCountryId) =>
    requireActorCountryScope({countryScope, actorCountryId});

const historyScope = (countryScope) => {
    const scope = String(countryScope ?? "").trim();
    if (!/^(?:0|[1-9]\d*)$/.test(scope)) {
        throw new Error("Некорректная страна для истории возвратов");
    }
    return scope;
};

const identifier = (value, label) => {
    const result = String(value ?? "").trim();
    if (!result) throw new Error(`${label} обязателен`);
    return result;
};

const positivePage = (value) => {
    const text = String(value ?? "").trim();
    if (!/^[1-9]\d*$/.test(text)) throw new Error("Страница должна быть положительной");
    return Number(text);
};

export const listRefundRequests = async ({countryScope, actorCountryId, status = "", limit = 50}) => {
    tenantScope(countryScope, actorCountryId);
    const response = await apiClient.get(REFUND_REQUESTS_PATH, {
        params: cleanParams({status, limit}),
    });
    return normalizeRefundRequestList(response.data);
};

export const getRefundRequest = async ({countryScope, actorCountryId, requestId}) => {
    tenantScope(countryScope, actorCountryId);
    const id = identifier(requestId, "ID запроса");
    const response = await apiClient.get(`${REFUND_REQUESTS_PATH}/${id}`);
    return normalizeRefundRequest(response.data);
};

export const createRefundRequest = async ({
    countryScope,
    actorCountryId,
    paymentBillId,
    payload,
    idempotencyKey,
    config = {},
}) => {
    tenantScope(countryScope, actorCountryId);
    const billId = identifier(paymentBillId, "ID платежа");
    const key = identifier(idempotencyKey, "Ключ идемпотентности");
    const response = await apiClient.post(
        `${REFUND_REQUESTS_PATH}/${billId}`,
        {...payload, idempotency_key: key},
        config,
    );
    return normalizeRefundRequest(response.data);
};

const decision = async ({countryScope, actorCountryId, requestId, action, payload}) => {
    tenantScope(countryScope, actorCountryId);
    const id = identifier(requestId, "ID запроса");
    const url = `${REFUND_REQUESTS_PATH}/${id}/${action}`;
    const response = payload === undefined
        ? await apiClient.post(url)
        : await apiClient.post(url, payload);
    return normalizeRefundRequest(response.data);
};

export const approveRefundRequest = ({countryScope, actorCountryId, requestId}) =>
    decision({countryScope, actorCountryId, requestId, action: "approve"});

export const rejectRefundRequest = ({countryScope, actorCountryId, requestId, reason}) =>
    decision({countryScope, actorCountryId, requestId, action: "reject", payload: {reason}});

export const cancelRefundRequest = ({countryScope, actorCountryId, requestId, reason}) =>
    decision({countryScope, actorCountryId, requestId, action: "cancel", payload: {reason}});

export const getRefundPolicy = async ({countryScope, actorCountryId}) => {
    tenantScope(countryScope, actorCountryId);
    const response = await apiClient.get(REFUND_POLICY_PATH);
    return normalizeRefundPolicy(response.data);
};

export const updateRefundPolicy = async ({countryScope, actorCountryId, payload}) => {
    tenantScope(countryScope, actorCountryId);
    const response = await apiClient.patch(REFUND_POLICY_PATH, payload);
    return normalizeRefundPolicy(response.data);
};

export const listPostedRefunds = async ({countryScope, filters = {}, page = 1}) => {
    const scope = historyScope(countryScope);
    const response = await apiClient.get(POSTED_REFUNDS_PATH, {
        params: cleanParams({...filters, country_id: scope, page: positivePage(page)}),
    });
    return normalizePostedRefundPage(response.data);
};

export const findPostedRefund = async ({
    countryScope,
    paymentBillId,
    postedRefundId,
}) => {
    const billId = identifier(paymentBillId, "ID платежа");
    const refundId = identifier(postedRefundId, "ID возврата");
    const visitedPages = new Set();
    let page = 1;

    while (page !== null) {
        if (visitedPages.has(page)) {
            throw new Error("История возвратов вернула зацикленную пагинацию");
        }
        visitedPages.add(page);
        const result = await listPostedRefunds({
            countryScope,
            page,
            filters: {payment_bill_id: billId},
        });
        const match = result.results.find(({id}) => id === refundId);
        if (match) return match;
        page = result.next;
    }

    return null;
};
